// Which pages of a PDF count as READ, given where they sit right now.
//
// Extracted from PdfSignPage so the rule can be tested: jsdom does not lay
// anything out, so getBoundingClientRect there is all zeroes and the real
// component can only be checked by eye. This is the part worth guarding — it
// gates whether someone is allowed to sign.
//
// A page is read when EITHER half of it is on screen, OR it fills most of the
// viewport. The second test exists because at fit-to-width an A4 page is about
// twice the viewer's height, so it can fill the screen completely and still
// only be ~44% "visible" by its own measure.

export const HALF_OF_PAGE = 0.5;
export const MOST_OF_VIEWPORT = 0.6;

// rects: [{ page, top, bottom }] in the same coordinate space as `root`.
// root: { top, bottom }
// atEnd: the viewer is scrolled to the bottom.
// lastPage: the highest page number, marked read when atEnd.
export function pagesSeenFrom({ rects = [], root, atEnd = false, lastPage = 0 } = {}) {
  const seen = new Set();
  const rootH = root ? root.bottom - root.top : 0;
  if (rootH > 0) {
    for (const r of rects) {
      const visibleH = Math.max(0, Math.min(r.bottom, root.bottom) - Math.max(r.top, root.top));
      if (visibleH <= 0) continue;
      const pageH = r.bottom - r.top;
      if (pageH <= 0) continue;
      if (visibleH / pageH >= HALF_OF_PAGE || visibleH / rootH >= MOST_OF_VIEWPORT) seen.add(r.page);
    }
  }
  // Scrolled to the end: the last page is as far into view as the document
  // allows, so it is read by definition. This is what the observer could never
  // conclude — see the note in PdfSignPage.
  if (atEnd && lastPage > 0) seen.add(lastPage);
  return seen;
}

// Which page the reader is ON — the one showing most, not whichever entry the
// observer happened to report last.
export function currentPageFrom({ rects = [], root } = {}) {
  let best = null;
  if (!root) return null;
  for (const r of rects) {
    const visibleH = Math.max(0, Math.min(r.bottom, root.bottom) - Math.max(r.top, root.top));
    if (visibleH <= 0) continue;
    if (!best || visibleH > best.visibleH) best = { page: r.page, visibleH };
  }
  return best ? best.page : null;
}
